/** API role permission shape (`module` + `actions`). */

export type ApiPermissionActions = {
  create?: boolean;
  read?: boolean;
  update?: boolean;
  delete?: boolean;
};

export type ApiPermissionItem = {
  module?: string;
  actions?: ApiPermissionActions;
};

export type ModuleCrud = {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
};

/** Map of normalized module key → CRUD flags. */
export type PermissionIndex = Map<string, ModuleCrud>;

export function normalizePermissionModule(module: string): string {
  return module.trim().toLowerCase();
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return Boolean(x && typeof x === 'object' && !Array.isArray(x));
}

function extractRoleName(user: Record<string, unknown>): string {
  const role = user.role;
  if (typeof role === 'string') return role;
  if (isRecord(role) && typeof role.name === 'string') return role.name;
  if (isRecord(role) && typeof role.roleName === 'string') return role.roleName;
  if (typeof user.roleName === 'string') return user.roleName;
  return '';
}

function isAdminRoleName(roleName: string): boolean {
  const normalized = roleName.trim().toLowerCase().replace(/[\s_-]+/g, '');
  return normalized === 'admin' || normalized === 'superadmin';
}

function coerceBool(v: unknown): boolean {
  return v === true;
}

function normalizeActions(raw: ApiPermissionActions | undefined | null): ModuleCrud {
  const a = raw ?? {};
  return {
    create: coerceBool(a.create),
    read: coerceBool(a.read),
    update: coerceBool(a.update),
    delete: coerceBool(a.delete),
  };
}

function isPermissionItem(x: unknown): x is ApiPermissionItem {
  return Boolean(x && typeof x === 'object' && 'module' in x);
}

function extractPermissionsArray(user: Record<string, unknown>): unknown[] | undefined {
  if (Array.isArray(user.permissions)) return user.permissions;
  const role = user.role;
  if (isRecord(role) && Array.isArray(role.permissions)) return role.permissions;
  return undefined;
}

/**
 * Returns `null` when no RBAC payload is present (legacy users: full access).
 * Returns a (possibly empty) map when permissions are defined on the user / role.
 */
export function parsePermissionsFromUser(user: unknown): PermissionIndex | null {
  if (!isRecord(user)) return null;
  const o = user;
  if (isAdminRoleName(extractRoleName(o))) return null;
  const arr = extractPermissionsArray(o);
  if (arr === undefined) return null;

  const map: PermissionIndex = new Map();
  for (const item of arr) {
    if (!isPermissionItem(item)) continue;
    const mod = typeof item.module === 'string' ? item.module : '';
    if (!mod) continue;
    const key = normalizePermissionModule(mod);
    map.set(key, normalizeActions(item.actions));
  }
  return map;
}

export function getModuleAccess(index: PermissionIndex | null, module: string): ModuleCrud {
  if (index === null) {
    return { create: true, read: true, update: true, delete: true };
  }
  const row = index.get(normalizePermissionModule(module));
  if (!row) {
    return { create: false, read: false, update: false, delete: false };
  }
  return row;
}
